import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import { useAuth } from '../AuthContext'
import { ApiError } from '../api/client'
import {
  COORDINATION_STATUSES,
  QUEUE_KEY,
  addNote,
  detailKey,
  fetchRequest,
  statusLabel,
  updateRequest,
  type CoordinationDetail,
  type CoordinationStatus,
  type CoordinationUpdate,
  type HospitalResponse,
} from '../api/emergencyCoordination'
import { DetailDrawer } from '../components/DetailDrawer'
import { StatusPill } from '../components/StatusPill'
import { REFETCH_MS } from '../coordinationTime'

/**
 * One coordination request, worked through the six-step checklist (M36-T07,
 * spec §C.2 and §F.1). The server enforces every rule (M36-T06); the page
 * offers only what the server will accept for this request and this role, and
 * shows the server's message at the top of the drawer when it refuses.
 *
 * Every form keeps what was typed until the server has accepted it: a refusal
 * never costs the coordinator their note.
 */

const STEP_LABELS = [
  'Accept and acknowledge',
  'Telephone the caller if needed',
  'Call the selected hospital',
  "Log the hospital's response",
  'Communicate the verified outcome',
  'Close and document',
] as const

const RESPONSE_LABELS: Record<HospitalResponse, string> = {
  no_answer: 'No answer',
  cannot_accept: 'Cannot accept',
  accepted: 'Accepted',
}

/** Free text the server takes up to this length (schemas/coordination.py). */
const TEXT_MAX = 2000
const NAME_MAX = 200
const NUMBER_MAX = 40

const INPUT =
  'w-full rounded-[8px] border border-line-2 bg-bg-app px-2 py-1.5 text-sm text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent'
const BUTTON =
  'inline-flex h-9 items-center justify-center rounded-[8px] border border-line-2 bg-bg-app px-3.5 text-sm font-medium text-ink-1 hover:bg-bg-soft disabled:opacity-50 aria-pressed:border-accent aria-pressed:bg-accent-bg aria-pressed:text-accent-ink'
const PRIMARY =
  'inline-flex h-9 items-center justify-center rounded-[8px] bg-accent px-3.5 text-sm font-semibold text-accent-on hover:opacity-90 disabled:opacity-50'

/** Save a change; `then` runs only once the server has accepted it. */
type Save = (update: CoordinationUpdate, then?: () => void) => void

function when(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString([], {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** A `datetime-local` value to an ISO time with a zone, or null. */
function isoFromLocal(value: string): string | null {
  if (!value) return null
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? null : d.toISOString()
}

export function CoordinationRequestDrawer({
  requestId,
  onClose,
}: {
  requestId: string | null
  onClose: () => void
}): ReactNode {
  const { token } = useAuth()
  const detail = useQuery({
    queryKey: detailKey(requestId ?? ''),
    queryFn: () => fetchRequest(token, requestId ?? ''),
    enabled: Boolean(token) && requestId !== null,
    // Two coordinators on one request see each other's changes.
    refetchInterval: REFETCH_MS,
  })

  return (
    <DetailDrawer
      open={requestId !== null}
      onClose={onClose}
      title={detail.data ? `Request ${detail.data.reference}` : 'Request'}
    >
      {/* Always visible on the detail (spec §C.2). */}
      <p
        role="note"
        className="mb-4 rounded-[10px] border border-bed-bad bg-bed-bad-bg px-3 py-2 text-sm font-semibold text-bed-bad"
      >
        Never display “hospital accepted” until a hospital staff member
        explicitly confirms it.
      </p>
      {detail.isPending ? (
        <div
          role="status"
          aria-label="Loading the request"
          className="h-40 rounded-xl bg-bg-sunken"
        />
      ) : detail.isError || !detail.data ? (
        <p role="alert" className="text-sm font-medium text-bed-bad">
          {detail.error instanceof ApiError
            ? detail.error.message
            : 'Could not load this request.'}
        </p>
      ) : (
        <RequestBody data={detail.data} />
      )}
    </DetailDrawer>
  )
}

function RequestBody({ data }: { data: CoordinationDetail }): ReactNode {
  const { token, user } = useAuth()
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: (update: CoordinationUpdate) =>
      updateRequest(token, data.id, update),
  })
  const save: Save = (update, then) => {
    setError(null)
    mutation.mutate(update, {
      onSuccess: (fresh) => {
        queryClient.setQueryData(detailKey(data.id), fresh)
        void queryClient.invalidateQueries({ queryKey: QUEUE_KEY })
        then?.()
      },
      onError: (e) =>
        setError(e instanceof ApiError ? e.message : 'Could not save.'),
    })
  }
  const busy = mutation.isPending

  // Reopening a closed request is an AMTC admin's (spec §D.2). A coordinator
  // is not offered anything that would reopen it.
  const closed = data.status === 'closed'
  const locked = closed && user?.role !== 'amtc_admin'

  const step = (n: number) => data.checklist.find((s) => s.step === n)
  const mapLink =
    data.lat !== null && data.lng !== null
      ? `https://www.google.com/maps?q=${data.lat},${data.lng}`
      : null

  return (
    <div className="flex flex-col gap-6 text-sm text-ink-1">
      {/* Refusals show HERE, at the top, where they are seen. */}
      {error ? (
        <p
          role="alert"
          className="rounded-[8px] border border-bed-bad/40 bg-bed-bad-bg px-3 py-2 font-medium text-bed-bad"
        >
          {error}
        </p>
      ) : null}

      <section aria-label="The request" className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill status={data.status} label={statusLabel(data.status)} />
          <span className="text-ink-3">Received {when(data.created_at)}</span>
        </div>
        {data.purged ? (
          <p className="rounded-[8px] bg-bg-soft px-3 py-2 text-ink-2">
            {data.purged_note}
          </p>
        ) : (
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
            <dt className="text-ink-3">Call back</dt>
            <dd>
              {data.callback_phone ? (
                <a
                  href={`tel:${data.callback_phone}`}
                  className="font-semibold text-accent-ink underline"
                >
                  {data.callback_phone}
                </a>
              ) : (
                '—'
              )}
            </dd>
            <dt className="text-ink-3">Where</dt>
            <dd>
              {data.location_text ?? 'Not given'}
              {mapLink ? (
                <>
                  {' · '}
                  <a
                    href={mapLink}
                    target="_blank"
                    rel="noreferrer"
                    className="text-accent-ink underline"
                  >
                    Open the map
                  </a>
                </>
              ) : null}
            </dd>
            <dt className="text-ink-3">What</dt>
            <dd className="whitespace-pre-wrap">
              {data.description ?? 'Not given'}
            </dd>
            <dt className="text-ink-3">Feedback</dt>
            <dd>
              {data.feedback_consent_at
                ? 'May be contacted for feedback'
                : 'No'}
            </dd>
          </dl>
        )}
        <p>
          <span className="text-ink-3">Hospital: </span>
          {data.hospital?.name ?? 'None chosen'}
        </p>
      </section>

      <Record data={data} />

      <section
        aria-labelledby="checklist-heading"
        className="flex flex-col gap-3"
      >
        <h3 id="checklist-heading" className="font-semibold">
          Checklist
        </h3>
        <ol className="flex flex-col gap-3">
          <li className="flex flex-col gap-1">
            <StepLine n={1} done={step(1)} />
            {step(1)?.done_at ? null : (
              <button
                type="button"
                className={PRIMARY}
                disabled={busy || locked}
                onClick={() => save({ acknowledge: true })}
              >
                Acknowledge (step 1)
              </button>
            )}
          </li>
          {[2, 3, 4, 5, 6].map((n) => (
            <li key={n} className="flex flex-col gap-1">
              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={Boolean(step(n)?.done_at)}
                  disabled={busy || locked}
                  onChange={(e) =>
                    save({
                      [`step${n}_done`]: e.target.checked,
                    } as CoordinationUpdate)
                  }
                />
                <StepLine n={n} done={step(n)} />
              </label>
              {n === 2 ? (
                <p className="ml-6 rounded-[8px] bg-bed-warn-bg px-2 py-1 font-semibold text-ink-1">
                  First ask: have you called 112?
                </p>
              ) : null}
              {n === 3 ? (
                <p className="ml-6">
                  {data.hospital?.verified_emergency_phone ? (
                    <a
                      href={`tel:${data.hospital.verified_emergency_phone}`}
                      className="font-semibold text-accent-ink underline"
                    >
                      Call {data.hospital.name}:{' '}
                      {data.hospital.verified_emergency_phone}
                    </a>
                  ) : (
                    <span className="text-ink-2">
                      {data.hospital
                        ? 'This hospital’s emergency number is not verified.'
                        : 'No hospital chosen.'}
                    </span>
                  )}
                </p>
              ) : null}
              {n === 4 ? (
                <ResponseControl
                  data={data}
                  save={save}
                  busy={busy}
                  locked={locked}
                />
              ) : null}
            </li>
          ))}
        </ol>
      </section>

      <ContactAttempts data={data} save={save} busy={busy} locked={locked} />
      {locked ? (
        <section
          aria-labelledby="status-heading"
          className="flex flex-col gap-1"
        >
          <h3 id="status-heading" className="font-semibold">
            Status
          </h3>
          <p className="text-ink-2">
            This request is closed. Only an AMTC administrator can reopen it.
          </p>
        </section>
      ) : (
        <StatusControl data={data} save={save} busy={busy} />
      )}

      <Notes data={data} />
      <History data={data} />
    </div>
  )
}

/** What has been recorded: what a coordinator taking over needs to see. */
function Record({ data }: { data: CoordinationDetail }): ReactNode {
  const rows: Array<[string, ReactNode]> = []
  if (data.hospital_response)
    rows.push(['Hospital response', RESPONSE_LABELS[data.hospital_response]])
  if (data.hospital_number_dialled)
    rows.push(['Number dialled', data.hospital_number_dialled])
  if (data.hospital_rep_name || data.hospital_rep_role)
    rows.push([
      'Confirmed by',
      [data.hospital_rep_name, data.hospital_rep_role]
        .filter(Boolean)
        .join(', '),
    ])
  if (data.hospital_confirmed_at)
    rows.push(['Confirmed at', when(data.hospital_confirmed_at)])
  if (data.unable_reason) rows.push(['Unable because', data.unable_reason])
  if (data.outcome_note) rows.push(['Outcome', data.outcome_note])
  if (data.closed_at) rows.push(['Closed', when(data.closed_at)])
  if (rows.length === 0) return null
  return (
    <section aria-labelledby="record-heading" className="flex flex-col gap-2">
      <h3 id="record-heading" className="font-semibold">
        Recorded so far
      </h3>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
        {rows.map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="text-ink-3">{label}</dt>
            <dd className="whitespace-pre-wrap">{value}</dd>
          </div>
        ))}
      </dl>
    </section>
  )
}

function StepLine({
  n,
  done,
}: {
  n: number
  done: { done_at: string | null; done_by_name: string | null } | undefined
}): ReactNode {
  return (
    <span>
      <span className="font-medium">
        {n}. {STEP_LABELS[n - 1]}
      </span>
      {done?.done_at ? (
        <span className="block text-xs text-ink-3">
          Done {when(done.done_at)}
          {done.done_by_name ? ` by ${done.done_by_name}` : ''}
        </span>
      ) : null}
    </span>
  )
}

/** Step 4. "Accepted" is not a response you can just tick: it opens the
 * "Hospital confirmed" form, which needs who confirmed it and when. A
 * confirmed request's response IS accepted; change its status to change it. */
function ResponseControl({
  data,
  save,
  busy,
  locked,
}: {
  data: CoordinationDetail
  save: Save
  busy: boolean
  locked: boolean
}): ReactNode {
  const [confirming, setConfirming] = useState(false)
  const confirmed = data.status === 'hospital_confirmed'
  const off = busy || locked || confirmed
  return (
    <div className="ml-6 flex flex-col gap-2">
      <p className="text-ink-2">
        Recorded:{' '}
        <span className="font-semibold text-ink-1">
          {data.hospital_response
            ? RESPONSE_LABELS[data.hospital_response]
            : 'nothing yet'}
        </span>
      </p>
      {confirmed ? (
        <p className="text-xs text-ink-3">
          Confirmed by the hospital. To record anything else, change the status
          first.
        </p>
      ) : null}
      <div
        role="group"
        aria-label="Hospital response"
        className="flex flex-wrap gap-2"
      >
        {(['no_answer', 'cannot_accept'] as const).map((r) => (
          <button
            key={r}
            type="button"
            className={BUTTON}
            aria-pressed={data.hospital_response === r}
            disabled={off}
            onClick={() => save({ hospital_response: r })}
          >
            {RESPONSE_LABELS[r]}
          </button>
        ))}
        <button
          type="button"
          className={BUTTON}
          aria-pressed={data.hospital_response === 'accepted'}
          disabled={off}
          onClick={() => setConfirming(true)}
        >
          Accepted (hospital confirmed)…
        </button>
      </div>
      {confirming && !confirmed ? (
        <ConfirmForm
          save={save}
          busy={busy}
          onCancel={() => setConfirming(false)}
          onSaved={() => setConfirming(false)}
        />
      ) : null}
    </div>
  )
}

/** The "Hospital confirmed" guard: will not save without all three. */
function ConfirmForm({
  save,
  busy,
  onCancel,
  onSaved,
}: {
  save: Save
  busy: boolean
  onCancel?: () => void
  onSaved?: () => void
}): ReactNode {
  const [name, setName] = useState('')
  const [role, setRole] = useState('')
  const [time, setTime] = useState('')
  const at = isoFromLocal(time)
  const ready = name.trim() !== '' && role.trim() !== '' && at !== null
  return (
    <fieldset className="flex flex-col gap-2 rounded-[10px] border border-line-2 p-3">
      <legend className="px-1 text-xs font-semibold text-ink-2">
        Hospital confirmed
      </legend>
      <label className="flex flex-col gap-1 text-xs font-medium text-ink-2">
        Name of the person who confirmed
        <input
          className={INPUT}
          value={name}
          maxLength={NAME_MAX}
          onChange={(e) => setName(e.target.value)}
        />
      </label>
      <label className="flex flex-col gap-1 text-xs font-medium text-ink-2">
        Their role
        <input
          className={INPUT}
          value={role}
          maxLength={NAME_MAX}
          onChange={(e) => setRole(e.target.value)}
        />
      </label>
      <label className="flex flex-col gap-1 text-xs font-medium text-ink-2">
        Time of the call
        <input
          type="datetime-local"
          className={INPUT}
          value={time}
          onChange={(e) => setTime(e.target.value)}
        />
      </label>
      {!ready ? (
        <p className="text-xs text-ink-3">
          All three are needed before this can be saved.
        </p>
      ) : null}
      <div className="flex gap-2">
        <button
          type="button"
          className={PRIMARY}
          disabled={!ready || busy}
          onClick={() => {
            if (!ready || at === null) return
            save(
              {
                status: 'hospital_confirmed',
                hospital_rep_name: name.trim(),
                hospital_rep_role: role.trim(),
                hospital_confirmed_at: at,
              },
              onSaved,
            )
          }}
        >
          Save: hospital confirmed
        </button>
        {onCancel ? (
          <button type="button" className={BUTTON} onClick={onCancel}>
            Cancel
          </button>
        ) : null}
      </div>
    </fieldset>
  )
}

function ContactAttempts({
  data,
  save,
  busy,
  locked,
}: {
  data: CoordinationDetail
  save: Save
  busy: boolean
  locked: boolean
}): ReactNode {
  const [note, setNote] = useState('')
  const reached = data.contact_attempts >= data.max_contact_attempts
  return (
    <section aria-labelledby="attempts-heading" className="flex flex-col gap-2">
      <h3 id="attempts-heading" className="font-semibold">
        Contact attempts: {data.contact_attempts} of {data.max_contact_attempts}
      </h3>
      {reached ? (
        <p className="rounded-[8px] bg-bed-warn-bg px-2 py-1 text-ink-1">
          {data.max_contact_attempts} attempts made. Consider “Unable to
          coordinate”, with the reason.
        </p>
      ) : null}
      {data.purged || locked ? null : (
        <label className="flex flex-col gap-1 text-xs font-medium text-ink-2">
          Note for this attempt (needed)
          <input
            className={INPUT}
            value={note}
            maxLength={TEXT_MAX}
            placeholder="e.g. 14:05 no answer"
            onChange={(e) => setNote(e.target.value)}
          />
        </label>
      )}
      <button
        type="button"
        className={BUTTON}
        disabled={busy || locked || (!data.purged && note.trim() === '')}
        onClick={() =>
          save(
            data.purged
              ? { status: 'contact_attempted' }
              : { status: 'contact_attempted', note: note.trim() },
            () => setNote(''),
          )
        }
      >
        Contact attempted
      </button>
    </section>
  )
}

/** Any status may follow any other (spec §D.2); each asks for what it needs. */
function StatusControl({
  data,
  save,
  busy,
}: {
  data: CoordinationDetail
  save: Save
  busy: boolean
}): ReactNode {
  const [next, setNext] = useState<CoordinationStatus | ''>('')
  const [dialled, setDialled] = useState(
    data.hospital?.verified_emergency_phone ?? '',
  )
  const [reason, setReason] = useState('')
  const [outcome, setOutcome] = useState('')
  const done = () => {
    setNext('')
    setReason('')
    setOutcome('')
  }

  const fields: {
    ready: boolean
    body: ReactNode
    update: CoordinationUpdate
  } = (() => {
    switch (next) {
      case 'hospital_contacted':
        return {
          ready: dialled.trim() !== '',
          update: { status: next, hospital_number_dialled: dialled.trim() },
          body: (
            <label className="flex flex-col gap-1 text-xs font-medium text-ink-2">
              Number you dialled
              <input
                className={INPUT}
                value={dialled}
                maxLength={NUMBER_MAX}
                onChange={(e) => setDialled(e.target.value)}
              />
            </label>
          ),
        }
      case 'unable_to_coordinate':
        return {
          ready: data.purged || reason.trim() !== '',
          update: data.purged
            ? { status: next }
            : { status: next, unable_reason: reason.trim() },
          body: data.purged ? null : (
            <label className="flex flex-col gap-1 text-xs font-medium text-ink-2">
              Reason
              <input
                className={INPUT}
                value={reason}
                maxLength={TEXT_MAX}
                onChange={(e) => setReason(e.target.value)}
              />
            </label>
          ),
        }
      case 'closed':
        return {
          ready: data.purged || outcome.trim() !== '',
          update: data.purged
            ? { status: next }
            : { status: next, outcome_note: outcome.trim() },
          body: data.purged ? null : (
            <label className="flex flex-col gap-1 text-xs font-medium text-ink-2">
              Outcome (what you told the caller)
              <textarea
                className={INPUT}
                rows={3}
                value={outcome}
                maxLength={TEXT_MAX}
                onChange={(e) => setOutcome(e.target.value)}
              />
            </label>
          ),
        }
      case '':
      case 'hospital_confirmed':
      case 'contact_attempted':
        return { ready: false, update: {}, body: null }
      default:
        return { ready: true, update: { status: next }, body: null }
    }
  })()

  return (
    <section aria-labelledby="status-heading" className="flex flex-col gap-2">
      <h3 id="status-heading" className="font-semibold">
        Status
      </h3>
      <label className="flex flex-col gap-1 text-xs font-medium text-ink-2">
        Change the status
        <select
          className={INPUT}
          value={next}
          onChange={(e) => setNext(e.target.value as CoordinationStatus | '')}
        >
          <option value="">Choose…</option>
          {COORDINATION_STATUSES.filter((s) => s !== data.status).map((s) => (
            <option key={s} value={s}>
              {statusLabel(s)}
            </option>
          ))}
        </select>
      </label>
      {next === 'hospital_confirmed' ? (
        <ConfirmForm save={save} busy={busy} onSaved={done} />
      ) : next === 'contact_attempted' ? (
        <p className="text-xs text-ink-3">
          Use “Contact attempted” above: it needs a note.
        </p>
      ) : next === '' ? null : (
        <>
          {fields.body}
          <button
            type="button"
            className={PRIMARY}
            disabled={!fields.ready || busy}
            onClick={() => save(fields.update, done)}
          >
            Save status
          </button>
        </>
      )}
    </section>
  )
}

function Notes({ data }: { data: CoordinationDetail }): ReactNode {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  const [text, setText] = useState('')
  const add = useMutation({
    mutationFn: () => addNote(token, data.id, text.trim()),
    onSuccess: () => {
      setText('')
      void queryClient.invalidateQueries({ queryKey: detailKey(data.id) })
    },
  })
  return (
    <section aria-labelledby="notes-heading" className="flex flex-col gap-2">
      <h3 id="notes-heading" className="font-semibold">
        Notes
      </h3>
      {data.notes.length === 0 ? (
        <p className="text-ink-3">No notes yet.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {data.notes.map((n) => (
            <li key={n.id} className="rounded-[8px] bg-bg-soft px-3 py-2">
              <p className="whitespace-pre-wrap">
                {n.body ?? 'Deleted after 90 days'}
              </p>
              <p className="text-xs text-ink-3">
                {n.author_name ?? 'Former staff'} · {when(n.created_at)}
              </p>
            </li>
          ))}
        </ul>
      )}
      {data.purged ? null : (
        <>
          <label className="flex flex-col gap-1 text-xs font-medium text-ink-2">
            Add a note (start with “Handover:” at the end of a shift)
            <textarea
              className={INPUT}
              rows={2}
              value={text}
              maxLength={TEXT_MAX}
              onChange={(e) => setText(e.target.value)}
            />
          </label>
          <button
            type="button"
            className={BUTTON}
            disabled={text.trim() === '' || add.isPending}
            onClick={() => add.mutate()}
          >
            Add note
          </button>
          {add.isError ? (
            <p role="alert" className="font-medium text-bed-bad">
              {add.error instanceof ApiError
                ? add.error.message
                : 'Could not add the note.'}
            </p>
          ) : null}
        </>
      )}
    </section>
  )
}

const ROLE_NAMES: Record<string, string> = {
  support: 'a coordinator',
  amtc_admin: 'an AMTC admin',
  system: 'Medcor (automatic)',
}

function History({ data }: { data: CoordinationDetail }): ReactNode {
  return (
    <section aria-labelledby="history-heading" className="flex flex-col gap-2">
      <h3 id="history-heading" className="font-semibold">
        Change history
      </h3>
      {data.history.length === 0 ? (
        <p className="text-ink-3">No changes yet.</p>
      ) : (
        <ul className="flex flex-col gap-1 text-xs text-ink-2">
          {data.history.map((h, i) => {
            const status =
              typeof h.after.status === 'string' ? h.after.status : null
            const what =
              h.action === 'emergency_coordination.note_added'
                ? 'added a note'
                : status
                  ? `set ${statusLabel(status)}`
                  : 'updated the request'
            return (
              <li key={i}>
                {when(h.occurred_at)} ·{' '}
                {h.actor_name ?? ROLE_NAMES[h.actor_role] ?? 'staff'} {what}
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
